import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { LoadingSpinner } from '../Layout';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

/**
 * The short intake, a couple of minutes, one question at a time, and only what
 * personalises the product today. Everything else FuturePath learns as you go.
 */
export const CoreIntake = ({ accent = '#22d3ee', onDone }) => {
  const { getAuthHeader } = useAuth();
  const [qs, setQs] = useState(null);
  const [answers, setAnswers] = useState({});
  const [n, setN] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/profile/core-intake`, getAuthHeader()).then((r) => {
      // Only what we don't already know, the basics step answered the rest.
      if (!r.data.questions?.length) return onDone();
      setQs(r.data.questions);
    }).catch(() => onDone());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!qs) return <div className="py-24 flex justify-center"><LoadingSpinner /></div>;

  const q = qs[n];
  const val = answers[q.key] ?? '';
  const set = (v) => setAnswers((a) => ({ ...a, [q.key]: v }));

  // Takes the answer map explicitly: on the last question the click that answers it also
  // finishes the intake, and reading state here would drop that final answer.
  const save = async (map = answers) => {
    setSaving(true);
    const facts = Object.entries(map)
      .filter(([, v]) => v !== '' && v !== null)
      .map(([key, value]) => ({ key, value, source: 'user' }));
    try { await axios.post(`${API}/profile/facts`, { facts }, getAuthHeader()); } catch { /* keep going */ }
    setSaving(false);
    onDone();
  };

  const next = (map) => (n < qs.length - 1 ? setN(n + 1) : save(map));
  // These few answers shape the whole experience, so they are not skippable.
  const canNext = Boolean(q.optional || (val !== '' && val !== null && val !== undefined));

  return (
    <div className="max-w-2xl mx-auto" data-testid="core-intake">
      <div className="flex items-center justify-between text-[11px] text-slate-300 mb-2">
        <span>A couple of minutes · question {n + 1} of {qs.length}</span>
        <span className="fp-num">{Math.round(((n + 1) / qs.length) * 100)}%</span>
      </div>
      <div className="fp-meter mb-8">
        <div className="fp-meter-fill transition-all duration-500"
          style={{ width: `${((n + 1) / qs.length) * 100}%`, background: `linear-gradient(90deg, ${accent}, #a78bfa)` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={q.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.28 }}>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white text-center tracking-tight">{q.label}</h2>
          <p className="text-sm text-slate-300 text-center mt-3">{q.why}</p>

          <div className="mt-8">
            {q.options && (
              <div className="flex flex-wrap justify-center gap-2.5" data-testid={`intake-options-${q.key}`}>
                {q.options.map((o) => (
                  <button key={o} onClick={() => { const m = { ...answers, [q.key]: o }; setAnswers(m); setTimeout(() => next(m), 220); }}
                    data-testid={`intake-${q.key}-${o.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                    className="px-4 py-2.5 rounded-full text-sm border transition-all duration-200 hover:-translate-y-0.5"
                    style={val === o
                      ? { borderColor: accent, background: `${accent}22`, color: '#fff', boxShadow: `0 0 28px -10px ${accent}` }
                      : { borderColor: 'rgba(148,163,184,0.25)', color: '#cbd5e1' }}>
                    {o}
                  </button>
                ))}
              </div>
            )}
            {q.type === 'state' && (
              <div className="flex flex-wrap justify-center gap-1.5" data-testid="intake-state">
                {STATES.map((st) => (
                  <button key={st} onClick={() => { const m = { ...answers, [q.key]: st }; setAnswers(m); setTimeout(() => next(m), 200); }}
                    data-testid={`intake-state-${st}`}
                    className="px-2.5 py-1.5 rounded-lg text-xs border transition-all duration-150"
                    style={val === st
                      ? { borderColor: accent, background: `${accent}22`, color: '#fff' }
                      : { borderColor: 'rgba(148,163,184,0.2)', color: '#94a3b8' }}>
                    {st}
                  </button>
                ))}
              </div>
            )}
            {q.type === 'text' && (
              <div className="max-w-xs mx-auto">
                <Input value={val} onChange={(e) => set(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && next(answers)}
                  placeholder="e.g. 27601" className="input-dark text-center" data-testid={`intake-text-${q.key}`} />
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-center gap-3 mt-10">
        {n > 0 && (
          <Button variant="ghost" onClick={() => setN(n - 1)} data-testid="intake-back" className="text-slate-300 rounded-full">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Back
          </Button>
        )}
        <Button onClick={() => next(answers)} disabled={saving || !canNext} data-testid="intake-next"
          className="rounded-full px-7 text-white" style={{ background: `linear-gradient(90deg, ${accent}, #a78bfa)` }}>
          {saving ? <LoadingSpinner size="sm" />
            : n === qs.length - 1 ? <>Done <Check className="w-4 h-4 ml-1.5" /></>
              : <>{q.optional && !val ? 'Skip' : 'Next'} <ArrowRight className="w-4 h-4 ml-1.5" /></>}
        </Button>
      </div>
      <p className="text-[11px] text-slate-300 text-center mt-5">
        That's all we ask up front. Everything else FuturePath picks up as you use it.
      </p>
    </div>
  );
};

export default CoreIntake;
